import { usersApi } from './authenticatedApi'
import {
  UserResponse,
  CreateUserRequest,
  UpdateUserRequest,
  BanUserRequest,
  VerifyPasswordRequest,
  VerifyPasswordResponse,
  ChangePasswordRequest,
  ChangePasswordResponse,
  UserRole,
  EntityType,
} from './generated/models'
import { PagedUserResponse } from './types'
import { retryWithBackoff } from '../utils'

interface DeleteUserOptions {
  onSuccess?: () => void
  onError?: (error: Error) => void
  onSettled?: () => void
}

interface UserListFilters {
  entityType?: EntityType
  entityId?: string
  email?: string
  role?: UserRole
  firstName?: string
  lastName?: string
  page?: number
  size?: number
}

export class UserService {
  private static instance: UserService

  private constructor() {}

  static getInstance(): UserService {
    if (!UserService.instance) {
      UserService.instance = new UserService()
    }
    return UserService.instance
  }

  async deleteUser(userId: string, options?: DeleteUserOptions): Promise<void> {
    try {
      await usersApi.deleteUserV1(userId)
      options?.onSuccess?.()
    } catch (error) {
      console.error('Delete user failed:', error)
      const finalError = error instanceof Error ? error : new Error('Failed to delete user')
      options?.onError?.(finalError)
      throw finalError
    } finally {
      options?.onSettled?.()
    }
  }

  async refreshUserList(filters?: UserListFilters): Promise<UserResponse[]> {
    try {
      const response = await usersApi.getUsersV1(
        filters?.email,
        filters?.role,
        filters?.entityType,
        filters?.entityId,
        filters?.firstName,
        filters?.lastName,
        filters?.page?.toString(),
        filters?.size,
      )
      return (response.data as PagedUserResponse).items
    } catch (error) {
      console.error('Failed to refresh user list:', error)
      throw error instanceof Error ? error : new Error('Failed to refresh user list')
    }
  }

  async getUserById(userId: string): Promise<UserResponse> {
    try {
      const response = await retryWithBackoff(() => usersApi.getUserByIdV1(userId))
      return response.data
    } catch (error) {
      console.error('Failed to get user details:', error)
      throw error instanceof Error ? error : new Error('Failed to get user details')
    }
  }

  async resendInvite(userId: string, redirectUrl?: string): Promise<UserResponse> {
    try {
      const response = await usersApi.resendUserInviteV1(userId, redirectUrl)
      return response.data
    } catch (error) {
      console.error('Failed to resend invite:', error)
      throw error instanceof Error ? error : new Error('Failed to resend invite')
    }
  }

  async banUser(userId: string, banUntil: Date): Promise<UserResponse> {
    try {
      const banRequest: BanUserRequest = { banUntil: banUntil.toISOString() }
      const response = await usersApi.banUserV1(userId, banRequest)
      return response.data
    } catch (error) {
      console.error('Failed to ban user:', error)
      throw error instanceof Error ? error : new Error('Failed to ban user')
    }
  }

  async unbanUser(userId: string): Promise<UserResponse> {
    try {
      const response = await usersApi.unbanUserV1(userId)
      return response.data
    } catch (error) {
      console.error('Failed to unban user:', error)
      throw error instanceof Error ? error : new Error('Failed to unban user')
    }
  }

  async deleteTotpDevice(userId: string): Promise<void> {
    try {
      await usersApi.deleteTotpV1(userId)
    } catch (error) {
      console.error('Failed to delete TOTP device:', error)
      throw error instanceof Error ? error : new Error('Failed to delete TOTP device')
    }
  }

  async createUser(request: CreateUserRequest): Promise<UserResponse> {
    try {
      const response = await usersApi.createUsersV1(request)
      return response.data
    } catch (error) {
      console.error('Failed to create user:', error)
      throw error instanceof Error ? error : new Error('Failed to create user')
    }
  }

  async updateUser(userId: string, request: UpdateUserRequest): Promise<UserResponse> {
    try {
      const response = await usersApi.updateUserByIdV1(userId, request)
      return response.data
    } catch (error) {
      console.error('Failed to update user:', error)
      throw error instanceof Error ? error : new Error('Failed to update user')
    }
  }

  async getCurrentUserProfile(): Promise<UserResponse> {
    try {
      const response = await usersApi.getUserProfileV1()
      return response.data
    } catch (error) {
      console.error('Failed to get user profile:', error)
      throw error instanceof Error ? error : new Error('Failed to get user profile')
    }
  }

  async verifyPassword(password: string): Promise<VerifyPasswordResponse> {
    try {
      const request: VerifyPasswordRequest = { password }
      const response = await usersApi.verifyPasswordV1(request)
      return response.data
    } catch (error) {
      console.error('Failed to verify password:', error)
      throw error instanceof Error ? error : new Error('Failed to verify password')
    }
  }

  async changePassword(currentPassword: string, newPassword: string): Promise<ChangePasswordResponse> {
    try {
      const request: ChangePasswordRequest = { currentPassword, newPassword }
      const response = await usersApi.changePasswordV1(request)
      return response.data
    } catch (error) {
      console.error('Failed to change password:', error)
      throw error instanceof Error ? error : new Error('Failed to change password')
    }
  }
}

export const userService = UserService.getInstance()
